import { useEffect, type FC } from 'react';

import VehicleThumbnail from '../../assets/images/vehicle-thumbnail.png';
import { UiStates } from '../../enums';
import { useUIManager } from '../../context/UIManagerContext';
import { useAuthContext } from '../../context/AuthContext';
import { generateIpfsSources, getKernelSigner } from '../../services/turnkeyService';
import { SimpleResult } from '../../models/resultTypes';
import {
  getPayloadToSign,
  mintVehicleWithSignature,
  waitForTokenId,
} from '../../services/dimoDevicesService';
import { IntegrationNft, MintVehicleNft } from '../../models/typedData';
import { SAMPLE_B64_IMAGE } from '../../utils/constants';
import { UIManagerLoaderWrapper } from '../Shared';

export const MintVehicle: FC = () => {
  const { componentData, setLoadingState, setUiState, setComponentData } = useUIManager();
  const { user, jwt, validateSession } = useAuthContext();

  useEffect(() => {
    const processMint = async () => {
      try {
        setLoadingState(true, 'Creating vehicle...', true);

        const { integrationID, userDeviceID } = componentData;

        if (!componentData || !integrationID || !userDeviceID) {
          return console.error('Error creating vehicle:');
        }

        const payloadToSign = await getPayloadToSign(
          {
            userDeviceID,
            integrationID,
          },
          jwt,
        );

        if (!payloadToSign.success || !payloadToSign.data)
          return console.error('Error creating vehicle:');

        //Sign payload using txn SDK
        const signature = await handleSign(payloadToSign.data);

        //Use signed payload + id's to send signed payload for minting
        if (!signature) {
          return console.error('Could not sign payload');
        }

        const mintedVehicle = await handleMint(signature, userDeviceID);

        if (!mintedVehicle.success) {
          return console.error('Could not mint vehicle');
        }

        //Poll for Token ID
        const tokenId = await waitForTokenId(userDeviceID, jwt);

        if (tokenId) {
          setComponentData({
            ...componentData,
            preSelectedVehicles: [tokenId.toString()],
          });
          setLoadingState(false);
          setUiState(UiStates.VEHICLE_MANAGER);
        }
      } catch (error) {
        console.error('Mint processing error:', error);
      } finally {
        setLoadingState(false);
      }
    };

    // eslint-disable-next-line
    processMint();
    // eslint-disable-next-line
  }, [componentData]);

  const handleSign = async (nft: IntegrationNft | MintVehicleNft) => {
    const isValidSession = await validateSession();
    if (!isValidSession) {
      throw new Error('User does not have a valid session');
    }
    const kernelSigner = getKernelSigner();
    const resp = await kernelSigner.signTypedData(nft);
    return resp;
  };

  const handleMint = async (
    signature: string,
    userDeviceID: string,
  ): Promise<SimpleResult> => {
    const isValidSession = await validateSession();
    if (!isValidSession) {
      throw new Error('User does not have a valid session');
    }
    const expiration = 2933125200; //Placeholder for sacd input
    const permissions = 0; //Placeholder for sacd input
    const owner = user.smartContractAddress;
    const ipfsRes = await generateIpfsSources([], owner, BigInt(expiration));

    return await mintVehicleWithSignature(
      {
        imageData: SAMPLE_B64_IMAGE,
        imageDataTransparent: SAMPLE_B64_IMAGE,
        sacdInput: {
          expiration,
          permissions,
          source: ipfsRes,
          grantee: '0xAb5801a7D398351b8bE11C439e05C5b3259aec9B', //Placeholder grantee, we don't actually grant permissions upon mint
        },
        signature,
      },
      userDeviceID,
      jwt,
    );
  };

  return (
    <UIManagerLoaderWrapper>
      <img
        style={{ height: '40px', width: '40px' }}
        className="rounded-full object-cover mr-4"
        src={VehicleThumbnail}
        alt="Vehicle Thumbnail"
      />
      <div className="flex flex-col items-center gap-2">
        <h2 className="text-gray-500 text-xl font-medium">No cars connected yet</h2>
        <p className="text-sm">Connect your car in the DIMO app to share permissions.</p>
      </div>
      <div className="flex flex-col items-center gap-6">
        <p className="text-xl font-medium">Download the DIMO app now</p>
        <div className="flex flex-row gap-6"></div>
      </div>
    </UIManagerLoaderWrapper>
  );
};
